import { useQuery } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError } from '@/api/http';
import { Skeleton, Stamp, ReadFailed } from '@/components/ui';
import { say } from '@/lib/vocabulary';
import { FAMILIES_REQUIRED } from '@/components/PromoteDetection';

/**
 * Why a detection concluded what it did, down to the evidence.
 *
 * The evidence-gate chart names a decision behind every detection, and the
 * decision route returns its question, its conclusion, how it was classified,
 * and the exact references it rested on - each of which resolves to a single
 * observation with its method and source. None of that was reachable, so
 * "1 of 2 kinds of check" was a verdict an operator had to take on trust.
 *
 * Evidence families are named as the check they represent, because the enum
 * is the machinery and the check is what a person is actually asking about.
 */

const FAMILY: Record<string, string> = {
  S3_OBJECT_METADATA: 'what S3 records about the file',
  PATH_NAMESPACE: 'where it sits in the folder tree',
  MIME_MAGIC: 'the bytes at the start of the file',
  ARCHIVE_STRUCTURE: 'how the archive is laid out inside',
  VENDOR_MANIFEST: 'a manifest the vendor itself wrote',
  SCHEMA_SIGNATURE: 'the shape of the data',
  NATIVE_IDENTIFIER: 'an identifier only that tool issues',
  CROSS_FILE_CONSISTENCY: 'agreement across several files',
};

const CLASSIFICATION: Record<string, string> = {
  FACT: 'stated by the source',
  DETERMINISTIC_DERIVATION: 'derived by a fixed rule',
  UNKNOWN: 'not determined',
  CONTRADICTION: 'the evidence disagreed',
};

const DIRECTION: Record<string, string> = {
  SUPPORTING: 'supports',
  CONTRADICTING: 'argues against',
  NEUTRAL: 'neither way',
};

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * A count the API reported, or null where it reported none.
 *
 * This returned 0 for anything absent, and the sentence beneath renders those
 * numbers as findings: "Counted 0 signals across 0 of 2 kinds of check" is a
 * measurement, stated over an evaluation object that was simply not there.
 * The repo has a whole test file for this principle and this was the one
 * place it was not applied.
 */
function num(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}

/**
 * Every reference on this page, in one request.
 *
 * This used to be one request per row, twenty-five of them, and the route
 * built for exactly this takes up to fifty ids and returns `missing` so an
 * unresolved reference can be NAMED. Resolving one at a time threw that
 * distinction away: a reference the backend deliberately withholds and one
 * that does not exist are indistinguishable from a failed request, and the
 * contract says those two are meant to be indistinguishable from each other
 * and nothing else.
 */
function useEvidence(refs: string[]) {
  const ids = refs.slice(0, 25);
  return useQuery({
    queryKey: ['evidence-batch', ids.join(',')],
    queryFn: ({ signal }) => backend.resolveEvidenceBatch(ids, signal),
    enabled: ids.length > 0,
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 300_000,
  });
}

/** The references a decision rests on, or none while it is still loading. */
function refsOf(d: Record<string, unknown> | undefined): string[] {
  return Array.isArray(d?.evidenceRefs) ? (d!.evidenceRefs as string[]) : [];
}

function EvidenceRow({ record: d }: { record: Record<string, unknown> }) {
  const family = str(d.family);
  const direction = str(d.direction);
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-line px-4 py-1.5 last:border-0">
      <span className="text-small text-ink">{FAMILY[family] ?? say(family)}</span>
      <span className="text-caption text-ink-3">{DIRECTION[direction] ?? say(direction)}</span>
      {/* The observation is the evidence itself: the thing that was seen in
          the bytes. It was the faintest text in the row, below the labels
          describing it, and it clipped to one line with a title revealing a
          different field, so a truncated observation could not be recovered. */}
      <span className="mono w-full break-words text-caption text-ink-2" title={str(d.observation) || str(d.sourceRef)}>
        {str(d.observation) || str(d.sourceRef)}
      </span>
    </div>
  );
}

export function DecisionDetail({ decisionId }: { decisionId: string }) {
  const q = useQuery({
    queryKey: ['decision', decisionId],
    queryFn: ({ signal }) => backend.resolveDecision(decisionId, signal),
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 300_000,
  });
  const d = ((q.data as { data?: unknown })?.data ?? q.data) as Record<string, unknown> | undefined;
  // Above every early return: a hook called conditionally changes the hook
  // order between renders, which is React error #310 and shipped a white
  // screen on the bucket page once already. The lint gate names it exactly.
  const evidence = useEvidence(refsOf(d));

  if (q.isPending) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );
  }
  if (q.isError || !d) {
    return <ReadFailed what={explainLine(q.error, 'read this decision')} onRetry={() => void q.refetch()} />;
  }

  const evaluation = (d.evaluation ?? {}) as Record<string, unknown>;
  const refs = refsOf(d);
  const classification = str(d.classification);
  const contradictions = num(evaluation.contradictions) ?? 0;
  const signals = num(evaluation.signals);
  const families = num(evaluation.families);
  const excluded = num(evaluation.correlatedExcluded) ?? 0;

  return (
    <div>
      <div className="border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-row text-ink">{str(d.question) || 'This decision'}</span>
          {/* A decision the evidence gate refused wore the same grey as one
              that was merely replaced by a later decision. */}
          <Stamp
            tone={str(d.status) === 'CURRENT' ? 'ok' : str(d.status) === 'REJECTED' ? 'bad' : 'mut'}
            className="ml-auto"
          >
            {say(str(d.status))}
          </Stamp>
        </div>
        <p className="mt-1 text-small text-ink-2">{str(d.conclusion)}</p>
        <p className="mt-1 text-caption text-ink-3">
          {/* "not yet verified" asserts a future the enum does not describe:
              INDETERMINATE means the evidence did not settle, and the branch
              also fired on a confidence that was simply absent. */}
          <span className={classification === 'CONTRADICTION' ? 'text-bad' : undefined}>
            {CLASSIFICATION[classification] ?? say(classification)}
          </span>
          {str(d.confidence) === 'VERIFIED'
            ? ', and verified'
            : str(d.confidence) === 'INDETERMINATE'
              ? ', and the evidence did not settle'
              : ''}
          .{' '}
          {signals === null || families === null ? (
            'This decision carries no evidence count.'
          ) : (
            <>
              Counted {signals.toLocaleString()} signals across {families} of {FAMILIES_REQUIRED} kinds of check
              {excluded > 0 ? `, with ${excluded.toLocaleString()} excluded for saying the same thing twice` : ''}
              {contradictions > 0 ? `, and ${contradictions.toLocaleString()} that disagreed` : ''}.
            </>
          )}
        </p>
      </div>
      {refs.length === 0 ? (
        <p className="px-4 py-3 text-caption text-ink-3">This decision records no evidence references.</p>
      ) : (
        <>
          {evidence.isPending ? (
            <Skeleton className="mx-4 my-1.5 h-5" />
          ) : evidence.isError ? (
            <ReadFailed
              what={explainLine(evidence.error, 'read this evidence')}
              onRetry={() => void evidence.refetch()}
            />
          ) : (
            <>
              {(evidence.data?.items ?? []).map((item, i) => (
                <EvidenceRow key={String((item as Record<string, unknown>).evidenceId ?? i)} record={item as Record<string, unknown>} />
              ))}
              {/* Named, rather than a row that failed to load. */}
              {(evidence.data?.missing ?? []).map((id) => (
                <p key={id} className="px-4 py-1.5 text-caption text-bad">
                  One reference did not resolve. It is either unknown to the backend or outside what this account may
                  read, and the contract makes those two deliberately indistinguishable.
                </p>
              ))}
            </>
          )}
          {refs.length > 25 && (
            <p className="border-t border-line px-4 py-2 text-caption text-ink-3">
              Showing 25 of {refs.length.toLocaleString()} references.
            </p>
          )}
        </>
      )}
    </div>
  );
}
